/**
 * Filesystem-aligned shortcut router (Next.js App Router inspired).
 * Route patterns mirror `app/api/` paths: `[param]` segments become `:param` matchers.
 */

import type { ShortcutRouteHandler } from './types.js';

import * as bridgesLocalShortcutHealth from '../app/api/bridges/local-shortcut/health/route.js';
import * as bridgesFilesList from '../app/api/bridges/files/list/route.js';
import * as bridgesFilesRead from '../app/api/bridges/files/read/route.js';
import * as bridgesFilesCodeNav from '../app/api/bridges/files/code-nav/route.js';
import * as sessionsSessionIdFilesList from '../app/api/sessions/[sessionId]/files/list/route.js';
import * as sessionsSessionIdFilesRead from '../app/api/sessions/[sessionId]/files/read/route.js';
import * as sessionsSessionIdFilesCodeNav from '../app/api/sessions/[sessionId]/files/code-nav/route.js';

interface RouteEntry {
  method: string;
  segments: string[];
  paramNames: string[];
  handler: ShortcutRouteHandler;
}

const routes: RouteEntry[] = [];

/** Convert an `app/api/…` relative path to URL segments (`[id]` → `:id`). */
function segmentsFromAppPath(appPath: string): { segments: string[]; paramNames: string[] } {
  const paramNames: string[] = [];
  const segments = appPath.split('/').filter(Boolean).map((seg) => {
    if (seg.startsWith('[') && seg.endsWith(']')) {
      const name = seg.slice(1, -1);
      paramNames.push(name);
      return `:${name}`;
    }
    return seg;
  });
  return { segments, paramNames };
}

function addAppRoute(method: string, appPath: string, handler: ShortcutRouteHandler): void {
  const { segments, paramNames } = segmentsFromAppPath(appPath);
  routes.push({ method, segments, paramNames, handler });
}

addAppRoute('GET', 'api/bridges/local-shortcut/health', bridgesLocalShortcutHealth.GET);
addAppRoute('GET', 'api/bridges/files/list', bridgesFilesList.GET);
addAppRoute('GET', 'api/bridges/files/read', bridgesFilesRead.GET);
addAppRoute('GET', 'api/bridges/files/code-nav', bridgesFilesCodeNav.GET);
addAppRoute('GET', 'api/sessions/[sessionId]/files/list', sessionsSessionIdFilesList.GET);
addAppRoute('GET', 'api/sessions/[sessionId]/files/read', sessionsSessionIdFilesRead.GET);
addAppRoute('GET', 'api/sessions/[sessionId]/files/code-nav', sessionsSessionIdFilesCodeNav.GET);

export function matchShortcutRoute(
  pathname: string,
  method: string,
): { params: Record<string, string>; handler: ShortcutRouteHandler } | null {
  const pathSegments = pathname.split('/').filter(Boolean);
  for (const route of routes) {
    if (route.method !== method) continue;
    if (pathSegments.length !== route.segments.length) continue;
    const params: Record<string, string> = {};
    let matched = true;
    for (let i = 0; i < route.segments.length; i++) {
      const seg = route.segments[i]!;
      const value = pathSegments[i]!;
      if (seg.startsWith(':')) {
        params[seg.slice(1)] = decodeURIComponent(value);
      } else if (seg !== value) {
        matched = false;
        break;
      }
    }
    if (matched) return { params, handler: route.handler };
  }
  return null;
}
